/** React */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** MUI */
import {
    Box,
    Grid,
    MenuItem,
    Stack,
    Typography,
} from "@mui/material";

/** Services */
import authService from "../../../services/auth";
import companyService from "../../../services/company";

/** Components */
import BaseButton from "../../ui/BaseButton";
import BaseSelect2 from "../../ui/BaseSelect2";
import businessActivity from "../../Js/businessActivity";
import countriesProject from "../../Js/countries";
import CustomFilledSelectCountries from "../../ui/CustomFilledSelectCountries";
import CustTextField from "../../ui/CustTextField";
import Flag from "react-world-flags";

const UpdateSubscription = (props) => {
    const { franchiseDetails, setTexto, setAlertType, setShow, setIsLoading } = props;

    const [t] = useTranslation("global");

    const [isError, setIsError] = useState(false);
    const [existingEmail, setExistingEmail] = useState(false);
    const [errorEmail, setErrorEmail] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(franchiseDetails.phone_number ?? "");
    const [phoneCode, setPhoneCode] = useState(franchiseDetails.phone_code ?? "");
    const [countryCode, setCountryCode] = useState("");
    const [updatedFranchiseDetails, setUpdatedFranchiseDetails] = useState({
        franchiseName: franchiseDetails.franchise_name ?? "",
        country: franchiseDetails.country_name ?? "",
        ecActivity: franchiseDetails.business_activity ?? "",
        monthlyPages: franchiseDetails.monthly_conversions ?? "",
        address: franchiseDetails.franchise_address ?? "",
        email: franchiseDetails.franchise_email ?? "",
        website: franchiseDetails.franchise_web ?? "",
    });
    const [sortedCountries, setSortedCountries] = useState([]);

    const conversionList = [
        { id: 1, name: "1-5 " + t("register.conversions") },
        { id: 2, name: "6-15 " + t("register.conversions") },
        { id: 3, name: "16-30 " + t("register.conversions") },
        { id: 4, name: "31-50 " + t("register.conversions") },
        { id: 5, name: "51-200 " + t("register.conversions") },
        { id: 6, name: "+200 " + t("register.conversions") },
    ];

    const bussinessActFinal = [];

    for (let i = 0; i < businessActivity.length; i++) {
        bussinessActFinal.push({
            id: businessActivity[i].id,
            name: t("dialog." + businessActivity[i].translation),
        });
    }

    const handleChange = (event) => {
        setUpdatedFranchiseDetails({
            ...updatedFranchiseDetails,
            [event.target.name]: event.target.value,
        });
    };

    useEffect(() => {
        const allCountries = [...countriesProject];

        allCountries.sort((a, b) => {
            if (sessionStorage.getItem("lng") === "en") {
                return a.name_en.localeCompare(b.name_en);
            } else {
                return a.name_es.localeCompare(b.name_es);
            }
        });
        const codeFilter = allCountries.find((country) => country.phone_code === franchiseDetails.phone_code);

        setSortedCountries(allCountries);
        setCountryCode(codeFilter?.country_code);
    }, [franchiseDetails, t]);

    const handleChangeCurrentEmail = async (event) => {
        setUpdatedFranchiseDetails({
            ...updatedFranchiseDetails,
            email: event.target.value,
        });
        verifyEmail(event.target.value);
    };

    const verifyEmail = async (email) => {
        if (email.trim() && /.+@.+\..+/.test(email)) {
            await authService
                .verifyExistingUser(email)
                .then((response) => {
                    if (
                        response.status === 200 &&
                        email !== franchiseDetails.email
                    ) {
                        setAlertType("error");
                        setExistingEmail(true);
                        setErrorEmail(true);
                        setShow(true);
                        setTexto(t("register.existingEmail"));
                    } else {
                        setExistingEmail(false);
                        setErrorEmail(false);
                    }
                })
                .catch((error) => {
                    if (error.response.status === 404) {
                        setExistingEmail(false);
                        setErrorEmail(false);
                    }
                });
        } else {
            setErrorEmail(true);
        }
    };

    const handleChangePhoneCode = (event) => {
        let codeFilter = sortedCountries.find(
            (country) => country.country_code === event.target.value
        );
        if (codeFilter) {
            setCountryCode(event.target.value);
            setPhoneCode(codeFilter.phone_code);
        }
    };

    const handleChangePhoneNumber = (event) => {
        if (!isNaN(event.target.value)) {
            setPhoneNumber(event.target.value);
        }
    };

    const updateFranchise = async () => {
        setIsLoading(true);
        if (updatedFranchiseDetails.franchiseName.trim()) {
            // Update
            const params = {
                franchise_id: franchiseDetails._id,
                franchise_name: updatedFranchiseDetails.franchiseName,
                country_name: updatedFranchiseDetails.country,
                business_activity: updatedFranchiseDetails.ecActivity,
                monthly_conversions:
                    updatedFranchiseDetails.monthlyPages !== ""
                        ? updatedFranchiseDetails.monthlyPages
                        : undefined,
                franchise_address: updatedFranchiseDetails.address ?? undefined,
                franchise_email:
                    updatedFranchiseDetails.email !== ""
                        ? updatedFranchiseDetails.email
                        : undefined,
                franchise_web:
                    updatedFranchiseDetails.website !== ""
                        ? updatedFranchiseDetails.website
                        : undefined,
                phone_code: phoneCode !== "" ? phoneCode : undefined,
                phone_number: phoneNumber !== "" ? phoneNumber : undefined,
            };

            await companyService
                .updateFranchise(params)
                .then(() => {
                    setTexto(t("inbox.successEdit"));
                    setAlertType("success");
                    setShow(true);
                })
                .catch((err) => {
                    console.log(err);
                    setTexto("Error");
                    setAlertType("error");
                    setShow(true);
                });
        } else {
            setIsError(true);
        }
        setIsLoading(false);
    };

    return (
        <>
            <Box
                sx={{
                    borderRadius: "24px",
                    px: { xs: 3, md: 10 },
                    py: { xs: 3, md: 4 },
                    minWidth: "980px",
                    backgroundColor: "#FFF",
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h2">
                            {t("register.business")}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustTextField
                            fullWidth
                            label={t("register.subsName")}
                            name="franchiseName"
                            required
                            onChange={handleChange}
                            value={updatedFranchiseDetails.franchiseName}
                            error={isError && updatedFranchiseDetails.franchiseName.trim() === ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustomFilledSelectCountries
                            label={t("register.country")}
                            value={updatedFranchiseDetails.country}
                            onChange={handleChange}
                            name="country"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <BaseSelect2
                            label={t("dialog.ecActivity")}
                            value={updatedFranchiseDetails.ecActivity}
                            onChange={handleChange}
                            name="ecActivity"
                            values={bussinessActFinal}
                            idName="id"
                            valueName="name"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <BaseSelect2
                            name="monthlyPages"
                            label={t("dialog.monthlyPages")}
                            values={conversionList}
                            idName="id"
                            valueName="name"
                            value={updatedFranchiseDetails.monthlyPages}
                            onChange={(event) => handleChange(event)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustTextField
                            fullWidth
                            label={t("converter.address")}
                            name="address"
                            onChange={handleChange}
                            value={updatedFranchiseDetails.address}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustTextField
                            fullWidth
                            label={t("register.email")}
                            name="email"
                            onChange={(event) =>
                                handleChangeCurrentEmail(event)
                            }
                            error={((errorEmail) && !/.+@.+\..+/.test(updatedFranchiseDetails.email)) || existingEmail}
                            value={updatedFranchiseDetails.email}
                            type="text"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustTextField
                            fullWidth
                            label={"Website"}
                            name="website"
                            onChange={handleChange}
                            value={updatedFranchiseDetails.website}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                                <BaseSelect2
                                    label={t("register.code")}
                                    name="phoneCode"
                                    value={countryCode ?? ""}
                                    onChange={(event) =>
                                        handleChangePhoneCode(event)
                                    }
                                    fullWidth
                                    renderValue={() => (
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            alignItems="center"
                                        >
                                            <Flag
                                                code={countryCode}
                                                height="12"
                                                width="20"
                                            />
                                            <Typography>
                                                {phoneCode}
                                            </Typography>
                                        </Stack>
                                    )}
                                >
                                    {sortedCountries.map((option) => (
                                        <MenuItem
                                            value={option.country_code}
                                            key={option.country_code}
                                        >
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                            >
                                                <Flag
                                                    code={
                                                        option.country_code
                                                    }
                                                    height="14"
                                                    width="22"
                                                />
                                                <Typography>
                                                    {sessionStorage.getItem("lng") === "en"
                                                        ? option.name_en
                                                        : option.name_es
                                                    }
                                                    {` (${option.phone_code})`}
                                                </Typography>
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </BaseSelect2>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <CustTextField
                                    fullWidth
                                    label={t("register.phone")}
                                    name="phoneNumber"
                                    onChange={(event) => handleChangePhoneNumber(event)}
                                    value={phoneNumber}
                                    type="text"
                                    autoComplete="one-time-code"
                                />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sx={{ display: "flex", justifyContent: "end" }}
                            >
                                <BaseButton
                                    onClick={updateFranchise}
                                    sizebutton="l"
                                    sx={{
                                        marginLeft: "auto !important",
                                        mt: 2
                                    }}
                                >
                                    {t("register.saveChanges")}
                                </BaseButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default UpdateSubscription;
